import { css, SerializedStyles } from '@emotion/react'
import { Icon, Text, Theme, useTheme } from 'bold-ui'

import { POI_ICON_SIZE, POI_LABEL_GAP, PoiLabelPosition } from './map-poi-label-placement'

interface PoiMarkerProps {
  name?: string
  labelPosition?: PoiLabelPosition
  labelLines?: string[]
}

export function PoiMarker(props: Readonly<PoiMarkerProps>) {
  const { name, labelPosition = 'right', labelLines } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  return (
    <div css={styles.root}>
      <div css={styles.iconWrapper}>
        <Icon icon='hospital' size={0.875} style={styles.icon} />
      </div>
      {name && (
        <div css={styles.labelContainer(labelPosition)}>
          {toLabelLines(labelLines ?? [name]).map(({ key, text }) => (
            <Text key={key} style={styles.label}>
              {text}
            </Text>
          ))}
        </div>
      )}
    </div>
  )
}

interface LabelLine {
  key: string
  text: string
}

function toLabelLines(lines: string[]): LabelLine[] {
  const occurrences = new Map<string, number>()

  return lines.map((text) => {
    const occurrence = occurrences.get(text) ?? 0
    occurrences.set(text, occurrence + 1)
    return { key: occurrence === 0 ? text : `${text}-${occurrence}`, text }
  })
}

const createStyles = (theme: Theme) => ({
  root: css`
    position: relative;
    width: ${POI_ICON_SIZE}px;
    height: ${POI_ICON_SIZE}px;
    white-space: nowrap;
  `,
  iconWrapper: css`
    width: ${POI_ICON_SIZE}px;
    height: ${POI_ICON_SIZE}px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${theme.pallete.gray.c40};
    border: 2px solid ${theme.pallete.surface.main};
    border-radius: 0.25rem;
    box-shadow: ${theme.shadows.outer[10]};
  `,
  icon: css`
    fill: ${theme.pallete.surface.main};
    flex-shrink: 0;
  `,
  labelContainer: (position: PoiLabelPosition): SerializedStyles => css`
    position: absolute;
    display: flex;
    flex-direction: column;
    ${
      position === 'right' &&
      css`
        left: calc(100% + ${POI_LABEL_GAP}px);
        top: 50%;
        transform: translateY(-50%);
        align-items: flex-start;
      `
    }
    ${
      position === 'top' &&
      css`
        bottom: calc(100% + ${POI_LABEL_GAP}px);
        left: 50%;
        transform: translateX(-50%);
        align-items: center;
      `
    }
    ${
      position === 'bottom' &&
      css`
        top: calc(100% + ${POI_LABEL_GAP}px);
        left: 50%;
        transform: translateX(-50%);
        align-items: center;
      `
    }
  `,
  label: css`
    font-size: 0.75rem;
    color: ${theme.pallete.gray.c10};
    -webkit-text-stroke: 3px ${theme.pallete.surface.main};
    paint-order: stroke fill;
  `,
})
